import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, num, dt, ROLE_LABEL } from "@/lib/format";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LiveFeed } from "@/components/LiveFeed";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  IndianRupee, Wallet, TrendingUp, Clock, Users, QrCode, Receipt, Layers,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null)
    return (
      <div className="grid gap-4 md:grid-cols-4">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
      </div>
    );
  if (data === false) return <p className="text-destructive">Failed to load dashboard.</p>;

  const cards = {
    super_admin: [
      { label: "Gross Volume", value: inr(data.gross_volume), icon: IndianRupee, accent: "primary", testid: "stat-volume" },
      { label: "Platform Revenue", value: inr(data.superadmin_earning), sub: "Your net earnings", icon: TrendingUp, accent: "success", testid: "stat-revenue" },
      { label: "Merchants", value: num(data.merchants), sub: `${num(data.distributors)} distributors`, icon: Users, accent: "secondary", testid: "stat-merchants" },
      { label: "Pending", value: num(data.pending_count), sub: `${num(data.success_count)} successful`, icon: Clock, accent: "warning", testid: "stat-pending" },
    ],
    admin: [
      { label: "Gross Volume", value: inr(data.gross_volume), icon: IndianRupee, accent: "primary", testid: "stat-volume" },
      { label: "Platform Revenue", value: inr(data.superadmin_earning), sub: "Across all merchants", icon: TrendingUp, accent: "success", testid: "stat-revenue" },
      { label: "Merchants", value: num(data.merchants), sub: `${num(data.distributors)} distributors`, icon: Users, accent: "secondary", testid: "stat-merchants" },
      { label: "Pending", value: num(data.pending_count), sub: `${num(data.success_count)} successful`, icon: Clock, accent: "warning", testid: "stat-pending" },
    ],
    distributor: [
      { label: "Downline Volume", value: inr(data.gross_volume), icon: IndianRupee, accent: "primary", testid: "stat-volume" },
      { label: "My Commission", value: inr(data.distributor_earning), sub: "From downline fees", icon: Wallet, accent: "success", testid: "stat-commission" },
      { label: "My Merchants", value: num(data.merchants), icon: Layers, accent: "secondary", testid: "stat-merchants" },
      { label: "Pending", value: num(data.pending_count), sub: `${num(data.success_count)} successful`, icon: Clock, accent: "warning", testid: "stat-pending" },
    ],
    merchant: [
      { label: "Total Collected", value: inr(data.gross_volume), icon: IndianRupee, accent: "primary", testid: "stat-volume" },
      { label: "Net Settlement", value: inr(data.merchant_net), sub: `Fee ${inr(data.platform_fee)}`, icon: Wallet, accent: "success", testid: "stat-net" },
      { label: "Active UPI IDs", value: `${num(data.active_vpa_count)} / ${num(data.vpa_count)}`, icon: QrCode, accent: "secondary", testid: "stat-vpa" },
      { label: "Pending", value: num(data.pending_count), sub: `${num(data.success_count)} successful`, icon: Clock, accent: "warning", testid: "stat-pending" },
    ],
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Overview</h2>
          <p className="text-sm text-muted-foreground">{ROLE_LABEL[user.role]} control center</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {(cards[user.role] || cards.super_admin).map((c) => <StatCard key={c.label} {...c} />)}      </div>

      <LiveFeed />

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold">Volume · last 7 days</h3>
            <span className="text-xs text-muted-foreground">{num(data.success_count)} successful</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.series} margin={{ left: -18, right: 6, top: 6 }}>
                <defs>
                  <linearGradient id="vol" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={56}
                  tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 13 }}
                  formatter={(v) => [inr(v), "Volume"]} />
                <Area type="monotone" dataKey="volume" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#vol)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Receipt className="h-4 w-4" /> Recent activity</h3>
          <div className="space-y-3">
            {data.recent.length === 0 && <p className="text-sm text-muted-foreground">No transactions yet.</p>}
            {data.recent.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3" data-testid={`recent-${t.order_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t.customer_name}</p>
                  <p className="text-xs text-muted-foreground font-mono truncate">{t.order_id} · {dt(t.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold font-mono">{inr(t.amount)}</p>
                  <StatusBadge status={t.status} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
